// INTERMISSION (§9.5, §14.2): first the Instant Replay overlay (VHS "REPLAY ◀◀" tag, "Hold
// [Enter]/(A)/tap to skip" and the skip state), then Tally + Vote: animated score bars with
// breakdown chips popping one by one (coin chimes, a crown bounce for the leader), the 3 vote
// cards with live counts and a timer ring, and the Underdog panel (§4.6) — mutator cards for the
// picker, "Pudding is choosing a mutator…" for everyone else.
import { TUNE } from '@shared/constants';
import { MutatorId, RoomPhase } from '@shared/ids';
import type { PlayerDTO, RoomStateDTO } from '@shared/protocol/messages';
import { countUp } from '../anim';
import type { LiveScreen, NavAction } from '../contracts';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { icon, identityBadge, identityHex, MUTATOR_ICONS } from '../icons';
import { MUTATOR_BLURBS, MUTATOR_NAMES, ordinal } from '../labels';
import { el, setHidden, setStyle, setText, svg, toggleClass } from '../ui';
import { vote, voteStrip } from './Lobby';
import { button } from '../widgets';
import { replayOverlay } from './ReplayOverlay';

const BAR_MS = 800;
const STAGGER_MS = 80;
const CHIP_MS = 260;
const RING_R = 22;
const RING_C = 2 * Math.PI * RING_R;

export function create(deps: UiDeps): LiveScreen {
  const room = deps.room;
  const title = el('div', { class: 'ribbon yellow', text: 'ROUND RESULTS' });
  const rowsBox = el('div', { class: 'tally-rows' });
  const board = el('div', { class: 'panel with-ribbon tally-board' }, title, rowsBox);

  const votes = voteStrip(deps, 'tally-votes');
  const ringArc = svg('circle', { cx: 26, cy: 26, r: RING_R, class: 'ring-arc', 'stroke-dasharray': RING_C.toFixed(2) });
  const ringNum = el('span', { class: 'ring-num' });
  const ring = el('div', { class: 'timer-ring' }, svg('svg', { viewBox: '0 0 52 52' }, svg('circle', { cx: 26, cy: 26, r: RING_R, class: 'ring-bg' }), ringArc), ringNum);
  const voteHint = el('div', { class: 'vote-hint' });
  const nextMut = el('div', { class: 'next-mutator' });
  // No cards to vote on before the Golden Round and after the last round: the panel says what is next.
  const voteRibbon = el('div', { class: 'ribbon sky', text: 'VOTE NEXT ROUND' });
  const nextIcon = el('span', { class: 'tn-icon' });
  const nextTitle = el('div', { class: 'tn-title' });
  const nextSub = el('div', { class: 'tn-sub' });
  const nextCard = el('div', { class: 'tally-next' }, nextIcon, el('div', { class: 'tn-text' }, nextTitle, nextSub));
  const votePanel = el('div', { class: 'panel with-ribbon tally-vote' },
    voteRibbon, ring, votes.el, voteHint, nextCard, nextMut);

  const underdogCards = el('div', { class: 'ud-cards' });
  const underdogTimer = el('i');
  const underdogTitle = el('div', { class: 'ud-title' });
  const underdogMsg = el('div', { class: 'ud-msg' });
  const underdogBar = el('div', { class: 'bar ud-bar' }, underdogTimer);
  const underdog = el('div', { class: 'panel with-ribbon underdog' },
    el('div', { class: 'ribbon grape-r', text: "UNDERDOG'S CHOICE" }), underdogTitle, underdogCards, underdogMsg, underdogBar);

  const replay = replayOverlay(deps);
  const content = el('div', { class: 'tally' }, board, el('div', { class: 'tally-side' }, votePanel, underdog));
  const menuBtn = button({ icon: 'pause', title: 'Menu', variant: 'cream', size: 'small', cls: 'round screen-menu', onClick: () => deps.nav.show('pause') });
  const root = el('div', { class: 'screen modal tally-screen' }, el('div', { class: 'sunburst' }), content, replay.el, menuBtn);

  let shownRoundId = -1;
  let wasReplaying = false;
  let udKey = '';
  let udStart = 0;
  const cancels: (() => void)[] = [];

  function players(dto: RoomStateDTO): PlayerDTO[] {
    return dto.players.filter((p): p is PlayerDTO => p !== null);
  }

  /** Builds the animated standings for this round (once per round). */
  function renderBoard(dto: RoomStateDTO): void {
    const end = room.roundEnd;
    const roundId = end?.roundId ?? -2;
    if (roundId === shownRoundId && rowsBox.childElementCount > 0) return;
    shownRoundId = roundId;
    for (const c of cancels.splice(0)) c();
    setText(title, dto.roundIndex + 1 >= dto.totalRounds ? 'FINAL RESULTS' : `ROUND ${dto.roundIndex + 1} RESULTS`);
    const colorblind = deps.settings().accessibility.colorblind;
    const results = new Map((end?.results ?? []).map((r) => [r.slot, r]));
    const list = players(dto).map((p) => {
      const r = results.get(p.slot);
      const gained = r?.points ?? 0;
      return { p, now: p.score, before: p.score - gained, gained, place: r?.place ?? 0, breakdown: r?.breakdown ?? [] };
    }).sort((a, b) => b.now - a.now || a.p.slot - b.p.slot);
    const top = Math.max(1, ...list.map((x) => x.now));
    const leader = list[0];
    rowsBox.replaceChildren(...list.map((x, i) => {
      const fill = el('i');
      const ghost = el('b', { class: 'gain-ghost' });
      const total = el('span', { class: 'tr-total', text: String(x.before) });
      const chips = el('span', { class: 'tr-chips' });
      // Teams (§4.5): a Red / Blue stripe on the row's edge.
      const team = x.p.team === 1 ? ' team-red' : x.p.team === 2 ? ' team-blue' : '';
      const row = el('div', { class: `tally-row${x.p.slot === room.slot ? ' me' : ''}${team}`, style: { 'animation-delay': `${i * STAGGER_MS}ms`, '--id': identityHex(x.p.identity, colorblind) } },
        el('span', { class: `tr-rank r${i + 1}`, text: String(i + 1) }),
        identityBadge(x.p.identity, colorblind),
        el('span', { class: 'tr-name ellipsis', text: x.p.name }),
        el('span', { class: 'tr-bar' }, el('span', { class: 'bar' }, ghost, fill), chips),
        total,
        x === leader && x.now > 0 ? el('span', { class: 'tr-crown' }, icon('crown')) : null);
      setStyle(fill, 'width', `${(100 * Math.max(0, x.before)) / top}%`);
      setStyle(ghost, 'width', `${(100 * Math.max(0, x.now)) / top}%`);
      const delay = 350 + i * STAGGER_MS;
      const t1 = window.setTimeout(() => {
        setStyle(fill, 'width', `${(100 * Math.max(0, x.now)) / top}%`);
        cancels.push(countUp(total, x.before, x.now, BAR_MS));
        x.breakdown.forEach((b, k) => {
          const t2 = window.setTimeout(() => {
            const chip = el('span', { class: `pts-chip${b.pts >= 3 ? ' big' : ''}${/BOUNTY|REVENGE/i.test(b.label) ? ' hot' : ''}`, text: `+${b.pts} ${b.label}` });
            chips.append(chip);
            deps.services.playUi('coin');
          }, k * CHIP_MS);
          cancels.push(() => clearTimeout(t2));
        });
        // Your own scoreless round still says how it went (a bare row looked like missing data).
        if (x.breakdown.length === 0 && x.p.slot === room.slot && x.place > 0) {
          chips.append(el('span', { class: 'pts-chip muted', text: `${ordinal(x.place)} place` }));
        }
        if (x === leader) {
          const t3 = window.setTimeout(() => row.classList.add('leader'), BAR_MS);
          cancels.push(() => clearTimeout(t3));
        }
      }, delay);
      cancels.push(() => clearTimeout(t1));
      return row;
    }));
  }

  function renderUnderdog(dto: RoomStateDTO): void {
    const ud = dto.underdog;
    setHidden(underdog, ud === null);
    if (!ud) return;
    const picker = dto.players[ud.slot];
    const mine = ud.slot === room.slot && !room.spectator;
    const key = `${ud.slot}:${ud.options.join(',')}:${ud.chosen}:${mine}`;
    if (key === udKey) return;
    if (!udKey.startsWith(`${ud.slot}:${ud.options.join(',')}`)) udStart = performance.now();
    udKey = key;
    const name = picker?.name ?? 'The underdog';
    // The pick timer runs only while the choice is open.
    setHidden(underdogBar, ud.chosen !== -1);
    if (ud.chosen !== -1) {
      setText(underdogTitle, ud.chosen === MutatorId.NONE ? `${mine ? 'You' : name} played it straight` : `${mine ? 'You' : name} picked`);
      underdogCards.replaceChildren(mutCard(ud.chosen as MutatorId, -2, true));
      setText(underdogMsg, ud.chosen === MutatorId.NONE ? 'No mutator next round.' : `Next round: ${MUTATOR_NAMES[ud.chosen as MutatorId].toUpperCase()}!`);
      deps.services.playUi('cardFlip');
      return;
    }
    if (mine) {
      setText(underdogTitle, "You're the underdog! Pick a twist:");
      underdogCards.replaceChildren(...ud.options.map((m, i) => mutCard(m, i, false)), mutCard(MutatorId.NONE, -1, false));
      setText(underdogMsg, '');
      const first = underdogCards.querySelector<HTMLElement>('button');
      if (first && !(document.activeElement instanceof HTMLElement && root.contains(document.activeElement) && document.activeElement.closest('.underdog'))) first.focus({ preventScroll: true });
    } else {
      setText(underdogTitle, `${name} is choosing a mutator…`);
      underdogCards.replaceChildren(...ud.options.map((m) => mutCard(m, -3, false)));
      setText(underdogMsg, '');
    }
  }

  function mutCard(m: MutatorId, index: number, chosen: boolean): HTMLElement {
    const inner = [el('span', { class: 'mc-icon' }, icon(MUTATOR_ICONS[m])), el('span', { class: 'mc-name', text: MUTATOR_NAMES[m] }), el('span', { class: 'mc-blurb', text: MUTATOR_BLURBS[m] })];
    if (index >= -1) {
      return el('button', { class: `mut-card${m === MutatorId.NONE ? ' none' : ''}`, attrs: { type: 'button' }, on: {
        click: () => {
          deps.services.playUi('cardFlip');
          deps.actions.send({ t: 'underdog', choice: index });
        },
      } }, ...inner);
    }
    return el('div', { class: `mut-card ${chosen ? 'chosen' : 'peek'}` }, ...inner);
  }

  /** The vote cards, or what comes next when there is nothing to vote on. */
  function renderNext(dto: RoomStateDTO): void {
    const voting = dto.cards.length > 0;
    setHidden(votes.el, !voting);
    setHidden(voteHint, !voting);
    setHidden(nextCard, voting);
    if (voting) {
      setText(voteRibbon, 'VOTE NEXT ROUND');
      return;
    }
    const last = dto.roundIndex + 1 >= dto.totalRounds;
    const golden = !last && dto.settings.golden && dto.roundIndex + 2 >= dto.totalRounds;
    setText(voteRibbon, last ? 'MATCH OVER' : 'UP NEXT');
    toggleClass(nextCard, 'golden', golden);
    nextIcon.replaceChildren(icon(last ? 'trophy' : golden ? 'golden' : 'star'));
    setText(nextTitle, last ? 'AWARDS & PODIUM' : golden ? 'GOLDEN ROUND' : `ROUND ${dto.roundIndex + 2}`);
    setText(nextSub, last ? 'Time to crown the silliest beans!' : golden ? 'Double points. Anyone can still win!' : 'Get ready…');
  }

  function render(): void {
    const dto = room.dto;
    if (!dto || dto.phase !== RoomPhase.INTERMISSION) return;
    // The score bars animate once the Instant Replay is over, not hidden under it.
    if (!replay.active()) renderBoard(dto);
    votes.render(dto);
    renderUnderdog(dto);
    renderNext(dto);
    const mut = dto.underdog === null && dto.nextMutator !== MutatorId.NONE ? dto.nextMutator : MutatorId.NONE;
    setHidden(nextMut, mut === MutatorId.NONE);
    if (mut !== MutatorId.NONE) nextMut.replaceChildren(icon(MUTATOR_ICONS[mut]), el('span', { text: `Next round: ${MUTATOR_NAMES[mut]}` }));
  }
  room.onChange(() => {
    if (!root.hidden) render();
  });

  function renderHints(): void {
    const dev = deps.services.input.device;
    voteHint.replaceChildren(...(dev === 'touch' ? [el('span', { text: 'Tap a card to vote' })]
      : dev === 'pad' ? [el('span', { text: 'Pick a card with ' }), el('span', { class: 'key pad neutral', text: '✚' }), el('span', { text: ' then ' }), actionCap(deps, 'confirm')]
        : [el('span', { text: 'Press ' }), actionCap(deps, 'emote1'), actionCap(deps, 'emote2'), actionCap(deps, 'emote3'), el('span', { text: ' or click to vote' })]));
  }

  const onKey = (e: KeyboardEvent): void => {
    if (e.repeat || replay.active()) return;
    const b = deps.services.input.bindings.get().keys;
    const card = [b.emote1, b.emote2, b.emote3].findIndex((pair) => pair.includes(e.code));
    if (card >= 0) {
      vote(deps, card);
      votes.cards()[card]?.focus({ preventScroll: true });
    }
  };

  return {
    el: root,
    onShow() {
      udKey = '';
      wasReplaying = false;
      render();
      renderHints();
      window.addEventListener('keydown', onKey);
    },
    onHide() {
      window.removeEventListener('keydown', onKey);
      for (const c of cancels.splice(0)) c();
      shownRoundId = -1;
    },
    onNav(action: NavAction) {
      if (replay.active() && action === 'confirm') {
        replay.skip();
        return true;
      }
      return replay.active() && action !== 'pause';
    },
    update() {
      const replaying = replay.update();
      toggleClass(root, 'replaying', replaying);
      const dto = room.dto;
      if (!dto) return;
      if (replaying) wasReplaying = true;
      else if (wasReplaying) {
        // The replay just ended: (re)play the standings reveal now that it is on screen.
        wasReplaying = false;
        shownRoundId = -1;
        if (dto.phase === RoomPhase.INTERMISSION) renderBoard(dto);
      }
      const total = TUNE.flow.intermissionTicks / 60;
      const left = room.secondsUntil(dto.phaseEndTick);
      setText(ringNum, String(Math.ceil(left)));
      ringArc.setAttribute('stroke-dashoffset', (RING_C * (1 - Math.min(1, left / total))).toFixed(1));
      toggleClass(ring, 'urgent', left <= 3);
      const ud = dto.underdog;
      if (ud && ud.chosen === -1) {
        // The pick closes at the server's `endTick` (WP3); older servers: the §4.6 timer from when it opened.
        const total = TUNE.underdog.timerTicks / 60;
        const leftSec = ud.endTick !== undefined ? room.secondsUntil(ud.endTick) : total - (performance.now() - udStart) / 1000;
        setStyle(underdogTimer, 'width', `${Math.max(0, Math.min(100, (100 * leftSec) / total))}%`);
      }
    },
  };
}
